import { useState } from "react";
import { toast } from "sonner";
import { ConfirmButton } from "./ConfirmButton";

const card = "rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 space-y-4 max-w-3xl";
const TYPE_LABELS = { suggested_prompt: "Chat quick-reply", rulebook_question: "Rulebook question" };

export const OptimizerView = ({ data, api, authHeaders, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [actingId, setActingId] = useState(null);
  if (!data) return <p className="text-[17px] font-medium text-[#D6D6D6]">Loading...</p>;

  const lastRun = (data.runs || [])[0];

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${api}/admin/optimizer/run`, { method: "POST", headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error();
      toast.success(d.proposals_added ? `Done — ${d.proposals_added} proposal(s) waiting below.` : "Done — nothing worth changing right now.");
      onChanged();
    } catch {
      toast.error("The run didn't go through.");
    }
    setBusy(false);
  };

  const act = async (id, action) => {
    setActingId(id);
    try {
      const res = await fetch(`${api}/admin/optimizer/proposals/${id}/${action}`, { method: "POST", headers: authHeaders });
      if (!res.ok) throw new Error();
      toast.success(action === "approve" ? "Applied — it's live." : "Dismissed.");
      onChanged();
    } catch {
      toast.error("That didn't go through.");
    }
    setActingId(null);
  };

  const revertSeo = async () => {
    const res = await fetch(`${api}/admin/seo/revert`, { method: "POST", headers: authHeaders });
    if (res.ok) { toast.success("Reverted."); onChanged(); }
    else toast.error("Nothing to revert to.");
  };

  return (
    <div className="space-y-6" data-testid="optimizer-view">
      <div className={card} data-testid="optimizer-header-card">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-base font-bold text-white">The nightly optimizer</p>
          <button onClick={runNow} disabled={busy} data-testid="optimizer-run-button"
            className="ml-auto rounded-xl bg-white text-black font-semibold text-[15px] px-4 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
            {busy ? "Reading the week..." : "Run it now"}
          </button>
        </div>
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          Every night at 2:30 it reads the week's conversations and enquiries, keeps the search listing current on its own,
          and queues any visible copy changes here for your approval. Everything it touches lands in the audit log.
        </p>
        {lastRun && (
          <div className="border-t border-[#2E2E2E] pt-3 space-y-2" data-testid="optimizer-last-run">
            <p className="text-[15px] font-semibold text-white">
              Last run {lastRun.date} · {lastRun.stats?.conversations ?? 0} conversations · {lastRun.stats?.enquiries ?? 0} enquiries read
            </p>
            {lastRun.insights && <p className="text-[15px] font-medium text-[#D6D6D6]">{lastRun.insights}</p>}
            {(lastRun.keywords || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {lastRun.keywords.map((k, i) => (
                  <span key={`${k}-${i}`} className="text-[15px] font-semibold border border-[#2E2E2E] text-[#D6D6D6] rounded-full px-3 py-0.5">{k}</span>
                ))}
              </div>
            )}
            {lastRun.error && (
              <p className="text-[15px] font-semibold text-[#FF5C5C]" data-testid="optimizer-run-error">
                That run hit an error and made no changes — try it again, or check the logs if it keeps happening.
              </p>
            )}
          </div>
        )}
      </div>

      <div className={card} data-testid="seo-card">
        <div className="flex items-center gap-2">
          <p className="text-base font-bold text-white">Search listing (applied automatically)</p>
          {data.seo?.previous && (
            <ConfirmButton onConfirm={revertSeo} testId="seo-revert-button" confirmLabel="Sure? Put the old one back"
              className="ml-auto flex items-center gap-1.5 text-[15px] font-semibold text-[#D6D6D6] hover:text-white transition-colors duration-150"
              confirmClassName="ml-auto flex items-center gap-1.5 text-[15px] font-semibold text-[#FF5C5C]">
              Revert
            </ConfirmButton>
          )}
        </div>
        <div className="rounded-xl border border-[#2E2E2E] bg-[#050505] p-4">
          <p className="text-[17px] font-bold text-[#38BDF8]" data-testid="seo-title">{data.seo?.title}</p>
          <p className="text-[15px] font-medium text-[#D6D6D6] mt-1" data-testid="seo-description">{data.seo?.description}</p>
          <p className="text-[15px] font-medium text-[#D6D6D6] mt-2">
            {data.seo?.updated_at ? `Last touched ${new Date(data.seo.updated_at).toLocaleString()}` : "Not touched yet — the defaults are live"}
          </p>
        </div>
      </div>

      <div className={card} data-testid="proposals-card">
        <p className="text-base font-bold text-white">Waiting on you ({(data.pending || []).length})</p>
        {(data.pending || []).length === 0 && (
          <p className="text-[15px] font-medium text-[#D6D6D6]">Nothing pending. The next run queues anything worth your eyes.</p>
        )}
        {(data.pending || []).map((p) => (
          <div key={p.id} className="rounded-xl border border-[#2E2E2E] bg-[#050505] p-4 space-y-2" data-testid="proposal-row">
            <p className="text-[15px] font-semibold text-[#F59E0B]">{TYPE_LABELS[p.type] || p.type}</p>
            {p.type === "suggested_prompt" ? (
              <div className="space-y-1">
                {p.current && <p className="text-[15px] font-medium text-[#D6D6D6] line-through">{p.current}</p>}
                <p className="text-[17px] font-semibold text-white">{p.proposed}</p>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-[17px] font-semibold text-white">{p.q}</p>
                <p className="text-[15px] font-medium text-[#D6D6D6]">{p.a}</p>
              </div>
            )}
            {p.reason && <p className="text-[15px] font-medium text-[#D6D6D6]">Why: {p.reason}</p>}
            <div className="flex gap-3 pt-1">
              <button onClick={() => act(p.id, "approve")} disabled={actingId === p.id} data-testid="proposal-approve-button"
                className="flex items-center gap-1.5 rounded-xl bg-white text-black font-semibold text-[15px] px-4 py-2 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
                Put it live
              </button>
              <button onClick={() => act(p.id, "dismiss")} disabled={actingId === p.id} data-testid="proposal-dismiss-button"
                className="flex items-center gap-1.5 rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-[15px] px-4 py-2 hover:text-white transition-colors duration-150">
                Dismiss
              </button>
            </div>
          </div>
        ))}
        {(data.resolved || []).length > 0 && (
          <details className="border-t border-[#2E2E2E] pt-3">
            <summary className="text-[15px] font-semibold text-[#D6D6D6] cursor-pointer">Recently handled ({data.resolved.length})</summary>
            <div className="mt-2 space-y-1.5">
              {data.resolved.map((p) => (
                <p key={p.id} className="text-[15px] font-medium text-[#D6D6D6]" data-testid="resolved-row">
                  <span className={p.status === "approved" ? "text-[#4ADE80] font-semibold" : "font-semibold"}>{p.status}</span>
                  {" · "}{TYPE_LABELS[p.type] || p.type} · {(p.proposed || p.q || "").slice(0, 80)}
                </p>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};
